import React, { useMemo } from "react"

import { cloneDeep } from "lodash-es"
import { useFormContext } from "react-hook-form"

import {
    AvailableModalityCoverageStatus,
    type AvailablePlanStatus,
    AvailablePlanType,
} from "@common-shared/config/coverage.config"
import { PolicyColumnKey } from "@common-shared/config/oneVerify/oneVerifyPoliciesColumns.config"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { DatePickerElement } from "@front-shared/elements/datePicker.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SelectElement } from "@front-shared/elements/select.element"
import { TooltipElement } from "@front-shared/elements/tooltip.element"

import { CHECKER_CONFIG } from "../../../config/checker.config"
import { COVERAGE_CONFIG } from "../../../config/coverage.config"
import { OVERRIDES_CONFIG } from "../../../config/overrides.config"
import { POLICIES_COLUMNS_CONFIG } from "../../../config/policiesColumns.config"
import { ExportReportHelper } from "../../../helpers/exportReport.helper"
import { LookupHelper } from "../../../helpers/lookup.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import type {
    ICoverageOverrideFormData,
    IOverrideDefaultData,
} from "../../../types/data.types"
import { InlinedOverrideFormBenefitStructureComponent } from "./inlinedOverrideFormBenefitStructure.component"
import { InlinedOverrideFormTPANameComponent } from "./inlinedOverrideFormTPAName.component"
import { InlinedOverrideFormTPATypeComponent } from "./inlinedOverrideFormTPAType.component"

export const InlinedOverrideFormFieldComponent: React.FunctionComponent<
    IInlinedOverrideFormFieldComponentProps
> = props => {
    const {
        register,
        control,
        unregister,
        setValue,
        watch,
        formState: { errors },
        clearErrors,
    } = useFormContext()

    const benefitStructure = watch("benefitStructure")

    const isOtherBenefitStructure = useMemo<boolean>(() => {
        return benefitStructure === OVERRIDES_CONFIG.defaultBenefitsStructure
    }, [benefitStructure])

    const isFieldAlreadyOverridden = (
        fieldDataKey: keyof ICoverageOverrideFormData,
        secondaryFieldDataKey?: keyof ICoverageOverrideFormData,
    ): boolean => {
        const value = props.defaultData.override?.[fieldDataKey]

        const secondaryValue = secondaryFieldDataKey
            ? props.defaultData.override?.[secondaryFieldDataKey]
            : null

        const setValue =
            typeof value === "number"
                ? UtilHelper.getNumericValueIfSet(
                      value,
                      secondaryValue === null ||
                          typeof secondaryValue === "undefined"
                          ? secondaryValue
                          : +secondaryValue,
                  )
                : value || secondaryValue

        return typeof setValue !== "undefined" && setValue !== null
    }

    const getFieldAlreadyOverriddenIndicator = (
        fieldDataKey: keyof ICoverageOverrideFormData,
        secondaryFieldDataKey?: keyof ICoverageOverrideFormData,
    ): JSX.Element | null => {
        return isFieldAlreadyOverridden(fieldDataKey, secondaryFieldDataKey) ? (
            <div className="mt-[6px] ml-[8px] h-[12px] w-[12px] bg-brand-greenHighlight rounded-full" />
        ) : null
    }

    const getWrappedField = (
        fieldDataKey: keyof ICoverageOverrideFormData,
        renderedField: JSX.Element,
        secondaryFieldDataKey?: keyof ICoverageOverrideFormData,
        tooltip?: string,
    ): JSX.Element => {
        return (
            <div className="flex items-start">
                {renderedField}

                {tooltip ? (
                    <TooltipElement
                        className="mt-[4px] ml-[4px]"
                        text={tooltip}
                    />
                ) : null}

                {getFieldAlreadyOverriddenIndicator(
                    fieldDataKey,
                    secondaryFieldDataKey,
                )}
            </div>
        )
    }

    const getRenderedField = (): JSX.Element | string | null => {
        let renderedField: JSX.Element | undefined

        let fieldDataKey: keyof ICoverageOverrideFormData | undefined
        let secondaryFieldDataKey: keyof ICoverageOverrideFormData | undefined

        let tooltip: string | undefined

        const hasMixedValues =
            props.fieldKey &&
            props.defaultData.bulkMultipleValues?.[props.fieldKey]?.length

        const mixedValuesPlaceholder = hasMixedValues
            ? "Mixed values"
            : undefined

        switch (props.fieldKey) {
            case PolicyColumnKey.COPAYMENT:
                fieldDataKey = "copayment"

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputPrefix="$"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.COINSURANCE:
                fieldDataKey = "coinsurance"

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputSuffix="%"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minPercentage,
                            max: FRONT_VALIDATION_CONFIG.maxPercentage,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.TOTAL_DEDUCTIBLE:
                fieldDataKey = "deductible"

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputPrefix="$"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.REMAINING_DEDUCTIBLE:
                fieldDataKey = "remainingDeductible"

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputPrefix="$"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.TOTAL_OOP_MAX:
                fieldDataKey = "oopMax"

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputPrefix="$"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.REMAINING_OOP_MAX:
                fieldDataKey = "remainingOopMax"

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputPrefix="$"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.BENEFIT_STRUCTURE:
                fieldDataKey = "benefitStructure"

                renderedField = (
                    <InlinedOverrideFormBenefitStructureComponent
                        className={props.className}
                        defaultBenefitStructure={
                            props.defaultData.form.benefitStructure
                        }
                        defaultCustomBenefitStructure={
                            props.defaultData.form.customBenefitStructure
                        }
                        isDisabled={props.isDisabled}
                        benefitStructureNotRequired={
                            props.benefitStructureNotRequired
                        }
                    />
                )

                break

            case PolicyColumnKey.PLAN_STATUS:
                fieldDataKey = "planStatus"

                renderedField = (
                    <SelectElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        size="small"
                        renderedOptions={LookupHelper.getRenderedOptionsFromObject<AvailablePlanStatus>(
                            COVERAGE_CONFIG.availablePlanStatusMapping,
                            OVERRIDES_CONFIG.notAllowedPlanStatusValues,
                        )}
                        allowClear
                        reactHookFormRegister={register}
                        reactHookFormUnregister={unregister}
                        reactHookFormSet={setValue}
                        validations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                        }}
                        reactHookFormErrors={errors}
                        onSelected={() => {
                            clearErrors(fieldDataKey)
                        }}
                        defaultValue={
                            props.defaultData.form.planStatus &&
                            !OVERRIDES_CONFIG.notAllowedPlanStatusValues.includes(
                                props.defaultData.form.planStatus,
                            )
                                ? COVERAGE_CONFIG.availablePlanStatusMapping[
                                      props.defaultData.form.planStatus
                                  ]
                                : undefined
                        }
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.PLAN_TYPE:
                fieldDataKey = "planType"

                renderedField = (
                    <SelectElement
                        className="w-[290px]"
                        name={fieldDataKey}
                        size="small"
                        renderedOptions={LookupHelper.getRenderedOptionsFromObject<AvailablePlanType>(
                            CHECKER_CONFIG.planTypeMapping,
                            COVERAGE_CONFIG.unsupportedPlanTypes,
                        )}
                        allowClear
                        reactHookFormRegister={register}
                        reactHookFormUnregister={unregister}
                        reactHookFormSet={setValue}
                        defaultValue={
                            props.defaultData.form.planType
                                ? CHECKER_CONFIG.planTypeMapping[
                                      props.defaultData.form.planType
                                  ]
                                : undefined
                        }
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.PLAN_BEGIN_END_DATE:
                fieldDataKey = "planBeginDate"
                secondaryFieldDataKey = "planEndDate"

                renderedField = (
                    <div className="flex items-center">
                        <DatePickerElement
                            className="w-[136px]"
                            name={fieldDataKey}
                            reactHookFormErrors={errors}
                            shouldValidate
                            size="small"
                            reactHookFormRegister={register}
                            reactHookFormUnregister={unregister}
                            reactHookFormSet={setValue}
                            allowClear
                            defaultValue={
                                props.defaultData.form[fieldDataKey] ||
                                undefined
                            }
                            disabled={props.isDisabled}
                            placeholder={mixedValuesPlaceholder}
                        />

                        <PrimaryText className="mx-[4px]">-</PrimaryText>

                        <DatePickerElement
                            className="w-[136px]"
                            name={secondaryFieldDataKey}
                            reactHookFormErrors={errors}
                            shouldValidate
                            size="small"
                            reactHookFormRegister={register}
                            reactHookFormUnregister={unregister}
                            reactHookFormSet={setValue}
                            allowClear
                            defaultValue={
                                props.defaultData.form[secondaryFieldDataKey] ||
                                undefined
                            }
                            disabled={props.isDisabled}
                            placeholder={mixedValuesPlaceholder}
                        />
                    </div>
                )

                break

            case PolicyColumnKey.COVERAGE_STATUS:
                fieldDataKey = "modalityCoverageStatus"

                renderedField = (
                    <SelectElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        size="small"
                        renderedOptions={LookupHelper.getRenderedOptionsFromObject<AvailableModalityCoverageStatus>(
                            COVERAGE_CONFIG.availableModalityCoverageStatusMapping,
                            OVERRIDES_CONFIG.notAllowedModalityCoverageStatusValues,
                        )}
                        allowClear
                        reactHookFormRegister={register}
                        reactHookFormUnregister={unregister}
                        reactHookFormSet={setValue}
                        defaultValue={
                            props.defaultData.form.modalityCoverageStatus &&
                            !OVERRIDES_CONFIG.notAllowedModalityCoverageStatusValues.includes(
                                props.defaultData.form.modalityCoverageStatus,
                            )
                                ? COVERAGE_CONFIG
                                      .availableModalityCoverageStatusMapping[
                                      props.defaultData.form
                                          .modalityCoverageStatus
                                  ]
                                : undefined
                        }
                        disabled={props.isDisabled}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.ELIGIBILITY_BEGIN_END_DATE:
                fieldDataKey = "eligibilityBeginDate"
                secondaryFieldDataKey = "eligibilityEndDate"

                renderedField = (
                    <div className="flex items-center">
                        <DatePickerElement
                            className="w-[136px]"
                            name={fieldDataKey}
                            reactHookFormErrors={errors}
                            shouldValidate
                            size="small"
                            reactHookFormRegister={register}
                            reactHookFormUnregister={unregister}
                            reactHookFormSet={setValue}
                            allowClear
                            defaultValue={
                                props.defaultData.form[fieldDataKey] ||
                                undefined
                            }
                            disabled={props.isDisabled}
                            placeholder={mixedValuesPlaceholder}
                        />

                        <PrimaryText className="mx-[4px]">-</PrimaryText>

                        <DatePickerElement
                            className="w-[136px]"
                            name={secondaryFieldDataKey}
                            reactHookFormErrors={errors}
                            shouldValidate
                            size="small"
                            reactHookFormRegister={register}
                            reactHookFormUnregister={unregister}
                            reactHookFormSet={setValue}
                            allowClear
                            defaultValue={
                                props.defaultData.form[secondaryFieldDataKey] ||
                                undefined
                            }
                            disabled={props.isDisabled}
                            placeholder={mixedValuesPlaceholder}
                        />
                    </div>
                )

                break

            case PolicyColumnKey.THIRD_PARTY_ADMINISTRATOR_NAME:
                fieldDataKey = "thirdPartyAdministratorName"

                renderedField = (
                    <InlinedOverrideFormTPANameComponent
                        defaultData={props.defaultData}
                        fieldDataKey={fieldDataKey}
                        isDisabled={props.isDisabled}
                        mixedValuesPlaceholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.THIRD_PARTY_ADMINISTRATOR_TYPE:
                fieldDataKey = "thirdPartyAdministratorType"

                renderedField = (
                    <InlinedOverrideFormTPATypeComponent
                        defaultData={props.defaultData}
                        fieldDataKey={fieldDataKey}
                        isDisabled={props.isDisabled}
                        mixedValuesPlaceholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.MEMBER_OBLIGATION:
                fieldDataKey = "memberObligation"

                tooltip = !isOtherBenefitStructure
                    ? OVERRIDES_CONFIG.tooltips.memberObligation
                    : undefined

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputPrefix="$"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled || !isOtherBenefitStructure}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break

            case PolicyColumnKey.PAYER_OBLIGATION:
                fieldDataKey = "payerObligation"

                tooltip = !isOtherBenefitStructure
                    ? OVERRIDES_CONFIG.tooltips.payerObligation
                    : undefined

                renderedField = (
                    <InputElement
                        className="w-[136px]"
                        name={fieldDataKey}
                        type="number"
                        size="small"
                        reactHookErrors={errors}
                        reactHookControl={control}
                        inputPrefix="$"
                        reactHookValidations={{
                            min: FRONT_VALIDATION_CONFIG.minAmount0,
                        }}
                        defaultValue={props.defaultData.form[fieldDataKey]}
                        disabled={props.isDisabled || !isOtherBenefitStructure}
                        placeholder={mixedValuesPlaceholder}
                    />
                )

                break
        }

        if (!renderedField || !fieldDataKey) {
            return props.fallback
        }

        if (!tooltip && props.fieldKey && hasMixedValues) {
            tooltip =
                "Mixed values: " +
                props.defaultData.bulkMultipleValues?.[props.fieldKey]
                    ?.map(value => {
                        const columnConfig = cloneDeep({
                            ...POLICIES_COLUMNS_CONFIG.columnsConfig[
                                props.fieldKey!
                            ],
                        })

                        if (columnConfig.formatting?.isCents) {
                            columnConfig.formatting.isCents = false
                        }

                        return ExportReportHelper.getFormattedColumnValue(
                            value,
                            columnConfig,
                        )
                    })
                    .join(", ")
        }

        return getWrappedField(
            fieldDataKey,
            renderedField,
            secondaryFieldDataKey,
            tooltip,
        )
    }

    return <div className={props.className}>{getRenderedField()}</div>
}

interface IInlinedOverrideFormFieldComponentProps {
    className?: string
    fieldKey?: PolicyColumnKey
    defaultData: IOverrideDefaultData
    fallback: JSX.Element | string | null
    isDisabled?: boolean
    benefitStructureNotRequired?: boolean // will pass it through to benefit structures component
}
